import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Btn, PageHeader } from "@/components/common";
import GalleryDetail from "@/components/foto/GalleryDetail";
import StayList from "@/components/foto/StayList";
import { Images } from "lucide-react";

const inp = "px-3 py-2 text-sm rounded-md border border-slate-300 bg-white";

function NewGallery({ isAdmin, onCreated }) {
  const [f, setF] = useState({ title: "", photographer_id: "" });
  const [phs, setPhs] = useState([]);
  useEffect(() => { if (isAdmin) api.get("/photographers").then((r) => setPhs(r.data)); }, [isAdmin]);
  const save = async () => {
    try { const r = await api.post("/photo-galleries", { ...f, photographer_id: f.photographer_id || null }); setF({ title: "", photographer_id: "" }); onCreated(r.data.id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="p-4 mb-4 flex flex-wrap items-end gap-2" data-testid="gallery-new">
      <input data-testid="gallery-new-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titolo (es. Snorkeling 3/11)" className={`${inp} w-72`} />
      {isAdmin && (
        <select data-testid="gallery-new-photographer" value={f.photographer_id} onChange={(e) => setF({ ...f, photographer_id: e.target.value })} className={inp}>
          <option value="">Fotografo: io</option>
          {phs.map((p) => <option key={p.id} value={p.id}>{p.name || p.username}</option>)}
        </select>
      )}
      <Btn data-testid="gallery-new-save" onClick={save}>Nuova galleria</Btn>
    </Card>
  );
}

function Galleries({ isAdmin }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => { api.get("/photo-galleries").then((r) => setList(r.data)); };
  useEffect(() => { load(); }, []);
  if (open) return <GalleryDetail gid={open} onBack={() => { setOpen(null); load(); }} />;
  return (
    <>
      <NewGallery isAdmin={isAdmin} onCreated={(id) => { load(); setOpen(id); }} />
      {list && list.length === 0 && <div className="text-sm text-slate-400 py-10 text-center" data-testid="gallery-empty">Nessuna galleria</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(list || []).map((g) => (
          <button key={g.id} data-testid={`gallery-card-${g.id}`} onClick={() => setOpen(g.id)} className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-sky-300 transition-colors">
            <div className="flex items-center gap-2"><Images size={16} className="text-sky-700" /><b className="text-slate-800 truncate">{g.title}</b></div>
            <div className="text-xs text-slate-500 mt-1">{g.photo_count} foto{g.video_count ? ` · ${g.video_count} video` : ""} · {g.photographer_name || "—"}</div>
            <div className="text-xs text-slate-400 mt-1">creata {g.created_at.slice(0, 10).split("-").reverse().join("/")}{g.expired ? " · scaduta" : ""}</div>
            <div className="text-xs mt-1 text-sky-700" data-testid={`gallery-rooms-${g.id}`}>{g.rooms?.length ? `Link inviato a: ${g.rooms.map((r) => r.label).join(", ")}` : "Non ancora assegnata a nessuna camera"}</div>
          </button>
        ))}
      </div>
    </>
  );
}

export default function FotoCloud() {
  const { isAdmin, isSuperAdmin, isPhotographer } = useAuth();
  const [tab, setTab] = useState(isSuperAdmin ? "camere" : "gallerie");
  if (!isAdmin && !isPhotographer) return <Navigate to="/calendario" replace />;
  return (
    <div data-testid="foto-cloud-page">
      <PageHeader title="Cloud foto" subtitle="Il fotografo carica foto e video. Lei segna le camere che pagano e manda solo a loro il link con tutte le foto del soggiorno. Disponibili 90 giorni." />
      {isSuperAdmin && (
        <div className="flex gap-2 mb-5">
          {[["camere", "Camere: pagato / non pagato"], ["gallerie", "Gallerie e caricamento"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`foto-tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-sky-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "camere" && isSuperAdmin ? <StayList /> : <Galleries isAdmin={isSuperAdmin} />}
    </div>
  );
}
